import { Doc, DocPath } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import {
  ComputedResultContainer,
  ComputedResultLine,
} from "@/components/_pages/ModelPage/Sidebar/ComputedResult";
import {
  acquisitionInvestorsPercentCommonEquityAtom,
  acquisitionInvestorsPercentPreferredEquityAtom,
  coInvestorsPercentPreferredEquityAtom,
  commonEquityTotalPercentAtom,
  preferredEquityTotalPercentAtom,
  promoteSearchersCarryPercentCommonEquityAtom,
  searchPhaseInvestorsPercentCommonEquityAtom,
  searchPhaseInvestorsPercentPreferredEquityAtom,
  searcherCoInvestPercentCommonEquityAtom,
  sellerRolloverPercentCommonEquityAtom,
  sellerRolloverPercentPreferredEquityAtom,
} from "@/financeModels/owasco/companyOwnership";
import {
  isEquityInvestorsIncludedAtom,
  isInvestorFundedSearchPhaseAtom,
  isSellerRollingOverEquityAtom,
} from "@/financeModels/owasco/general";
import {
  formatDecimalPercent,
  formatDecimalPercentTwoPlaces,
} from "@/utils/format";
import { toNumberOrNull } from "@/utils/math";
import { Button, Divider, Group, Text } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { useState } from "react";
import { MdTableView } from "react-icons/md";
import { Link } from "react-router-dom";
import { openDebugOwnershipTablesModal } from "./DebugOwnershipTable";

type LineItem = {
  label: string;
  value: number | null;
  docPath: DocPath;
  visible: boolean;
};

export const OwnershipTable = () => {
  const [openDebugClickCounters, setOpenDebugClickCounters] = useState(0);

  const searchPhaseInvestorsPercentPreferredEquity = useAtomValue(
    searchPhaseInvestorsPercentPreferredEquityAtom,
  );
  const acquisitionInvestorsPercentPreferredEquity = useAtomValue(
    acquisitionInvestorsPercentPreferredEquityAtom,
  );
  const coInvestorsPercentPreferredEquity = useAtomValue(
    coInvestorsPercentPreferredEquityAtom,
  );
  const sellerRolloverPercentPreferredEquity = useAtomValue(
    sellerRolloverPercentPreferredEquityAtom,
  );
  const searchPhaseInvestorsPercentCommonEquity = useAtomValue(
    searchPhaseInvestorsPercentCommonEquityAtom,
  );
  const acquisitionInvestorsPercentCommonEquity = useAtomValue(
    acquisitionInvestorsPercentCommonEquityAtom,
  );
  const searcherCoInvestPercentCommonEquity = useAtomValue(
    searcherCoInvestPercentCommonEquityAtom,
  );
  const sellerRolloverPercentCommonEquity = useAtomValue(
    sellerRolloverPercentCommonEquityAtom,
  );
  const promoteSearchersCarryPercentCommonEquity = Number(
    useAtomValue(promoteSearchersCarryPercentCommonEquityAtom),
  );

  const preferredEquityTotalPercent = useAtomValue(
    preferredEquityTotalPercentAtom,
  );
  const commonEquityTotalPercent = useAtomValue(commonEquityTotalPercentAtom);

  const [isInvestorIncluded] = useAtom(isEquityInvestorsIncludedAtom);
  const [isSellerRollingOverEquity] = useAtom(isSellerRollingOverEquityAtom);
  const [investorsFundedSearchPhase] = useAtom(isInvestorFundedSearchPhaseAtom);

  const preferredItems: LineItem[] = [
    {
      label: "Search Phase Investors",
      value: searchPhaseInvestorsPercentPreferredEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipSearchPhaseInvestorsPercentPreferredEquity.mdx",
      visible: investorsFundedSearchPhase,
    },
    {
      label: "Acquisition Investors",
      value: acquisitionInvestorsPercentPreferredEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipAcquisitionInvestorsPercentPreferredEquity.mdx",
      visible: isInvestorIncluded,
    },
    {
      label: "Searcher Co-Invest",
      value: coInvestorsPercentPreferredEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipCoInvestorsPercentPreferredEquity.mdx",
      visible: true,
    },
    {
      label: "Seller Rollover",
      value: sellerRolloverPercentPreferredEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipSellerRolloverPercentPreferredEquity.mdx",
      visible: isSellerRollingOverEquity,
    },
  ];

  const commonItems: LineItem[] = [
    {
      label: "Search Phase Investors",
      value: searchPhaseInvestorsPercentCommonEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipSearchPhaseInvestorsPercentCommonEquity.mdx",
      visible: investorsFundedSearchPhase,
    },
    {
      label: "Acquisition Investors",
      value: acquisitionInvestorsPercentCommonEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipAcquisitionInvestorsPercentCommonEquity.mdx",
      visible: isInvestorIncluded,
    },
    {
      label: "Searcher Co-Invest",
      value: searcherCoInvestPercentCommonEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipSearcherCoInvestPercentCommonEquity.mdx",
      visible: true,
    },
    {
      label: "Seller Rollover",
      value: sellerRolloverPercentCommonEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipSellerRolloverPercentCommonEquity.mdx",
      visible: isSellerRollingOverEquity,
    },
    {
      label: "Promote (Searcher's Carry)",
      value: promoteSearchersCarryPercentCommonEquity,
      docPath:
        "@/docs/pages/model/sidebar/companyOwnershipPromoteSearchersCarryPercentCommonEquity.mdx",
      visible: true,
    },
  ];

  return (
    <ComputedResultContainer>
      <Group justify="space-between" gap={0} align="center" mb={"lg"}>
        <Text size="sm" ta="left" lh={1} mb={3}>
          <LabelExplainer
            inline
            label="Ownership Breakdown"
            explanation={
              <Doc path="@/docs/pages/model/sidebar/financeStructureCompanyOwnership.mdx" />
            }
          />
        </Text>
        <Button
          component={Link}
          to={window.location.href.split("/").slice(0, -1).join("/") + "/roi"}
          size="compact-xs"
          leftSection={<MdTableView size={20} />}
        >
          Full Cap Table
        </Button>
      </Group>
      <Text size="sm" c="dimmed" fw={"bold"}>
        <LabelExplainer
          label="Preferred Equity:"
          explanation={
            <Doc path="@/docs/pages/model/sidebar/companyOwnershipPreferredEquity.mdx" />
          }
        />
      </Text>
      <Divider />
      {preferredItems
        .filter((item) => item.visible)
        .map((item) => (
          <ComputedResultLine
            key={item.label}
            label={
              <LabelExplainer
                label={item.label}
                explanation={<Doc path={item.docPath} />}
              />
            }
            value={
              toNumberOrNull(item.value) !== null
                ? formatDecimalPercentTwoPlaces(Number(item.value))
                : null
            }
          />
        ))}
      <Divider />
      <ComputedResultLine
        label={""}
        value={formatDecimalPercent(preferredEquityTotalPercent)}
      />
      <Text size="sm" c="dimmed" fw={"bold"}>
        <LabelExplainer
          label="Common Equity:"
          explanation={
            <Doc path="@/docs/pages/model/sidebar/companyOwnershipCommonEquity.mdx" />
          }
        />
      </Text>
      <Divider />
      {commonItems
        .filter((item) => item.visible)
        .map((item) => (
          <ComputedResultLine
            key={item.label}
            label={
              <LabelExplainer
                label={item.label}
                explanation={<Doc path={item.docPath} />}
              />
            }
            value={
              toNumberOrNull(item.value) !== null
                ? formatDecimalPercentTwoPlaces(Number(item.value))
                : null
            }
          />
        ))}
      <Divider />
      <ComputedResultLine
        label={
          <div
            onClick={() => {
              setOpenDebugClickCounters(openDebugClickCounters + 1);
              //if divisble by 5, open debug tables
              if ((openDebugClickCounters + 1) % 5 === 0) {
                openDebugOwnershipTablesModal();
              }
            }}
            style={{
              //color: "transparent",
              background: "transparent",
              width: 100,
              height: 20,
            }}
          ></div>
        }
        value={formatDecimalPercent(commonEquityTotalPercent)}
      />
    </ComputedResultContainer>
  );
};
